import { useCallback, useEffect, useState } from "react";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import PageBreadcrumb from "../../../shared/PageBreadCrumb";
import CustomerTable from "../components/CustomerTable";
import CustomerForm from "../components/CustomerForm";
import CustomerDetailView from "../components/CustomerDetailView";
import { Customer, CreateCustomerData, customerService } from "../../../services/customerService";

const ITEMS_PER_PAGE = 6;

export default function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCustomers, setTotalCustomers] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);
  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [customerType, setCustomerType] = useState<"LLP" | "Pvt Ltd" | "Proprietor" | "Public Ltd" | "Partnership" | "Sole Proprietorship" | "Other" | "">("");
  const [status, setStatus] = useState<"active" | "inactive" | null>(null);
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: {
        page: number;
        limit: number;
        'sort-by': string;
        'sort-order': "asc" | "desc";
        search?: string;
        'customer-type'?: "LLP" | "Pvt Ltd" | "Proprietor" | "Public Ltd" | "Partnership" | "Sole Proprietorship" | "Other";
        status?: "active" | "inactive";
      } = {
        page,
        limit: ITEMS_PER_PAGE,
        'sort-by': sortField,
        'sort-order': sortOrder,
      };

      if (search) params.search = search;
      if (customerType) params['customer-type'] = customerType as "LLP" | "Pvt Ltd" | "Proprietor" | "Public Ltd" | "Partnership" | "Sole Proprietorship" | "Other";
      if (status) params.status = status;

      const result = await customerService.getCustomers(params);
      setCustomers(result.data);
      setTotalCustomers('meta' in result ? result.meta?.totalDocs || 0 : result.data.length);
    } catch (err: any) {
      console.error("Failed to fetch customers", err);
      setError(err.message || "Failed to fetch customers");
      setCustomers([]);
      setTotalCustomers(0);
    } finally {
      setLoading(false);
    }
  }, [search, customerType, status, page, sortField, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleAdd = async (customerData: CreateCustomerData) => {
    try {
      if (editingCustomer && editingCustomer._id) {
        // Update existing customer
        await customerService.updateCustomer(editingCustomer._id, customerData);
      } else {
        // Create new customer
        await customerService.createCustomer(customerData);
      }
      setEditingCustomer(null);
      setOpenForm(false);
      // Refetch data from backend to ensure consistency
      fetchCustomers();
    } catch (error: any) {
      console.error("Failed to save customer:", error);
      setError(error.message || "Failed to save customer");
      throw error; // Re-throw to let CustomerForm handle the error display
    }
  };

  const handleView = (customer: Customer) => {
    setViewingCustomer(customer);
    setShowDetailView(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setOpenForm(true);
  };

  const handleDelete = async (customer: Customer) => {
    try {
      if (customer._id) {
        await customerService.inactivateCustomer(customer._id);
        setCustomers((prev) => prev.map((c) =>
          c._id === customer._id ? { ...c, status: 'inactive' } : c
        ));
        // Refresh to get updated data
        fetchCustomers();
      }
    } catch (error: any) {
      console.error("Failed to delete customer:", error);
      setError(error.message || "Failed to delete customer");
    }
  };

  const handleToggleStatus = async (customer: Customer) => {
    try {
      if (customer._id) {
        if (customer.status === 'active') {
          await customerService.inactivateCustomer(customer._id);
        } else {
          await customerService.activateCustomer(customer._id);
        }
        // Refresh to get updated data
        fetchCustomers();
      }
    } catch (error: any) {
      console.error("Failed to toggle customer status:", error);
      setError(error.message || "Failed to toggle customer status");
    }
  };

  return (
    <div className="max-w-9xl mx-auto space-y-6 relative">
      {/* Page Header */}
      <PageBreadcrumb pageTitle="Customer Management" />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Customer Table */}
      <CustomerTable
        customers={customers}
        search={search}
        setSearch={setSearch}
        customerType={customerType}
        setCustomerType={(val: string) => setCustomerType(val as any)}
        status={status}
        setStatus={(val: string | null) => setStatus(val as any)}
        page={page}
        setPage={setPage}
        totalCustomers={totalCustomers}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onAdd={() => {
          setEditingCustomer(null);
          setOpenForm(true);
        }}
        onRefresh={fetchCustomers}
        sortField={sortField}
        sortOrder={sortOrder}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        loading={loading}
      />

      {/* Customer Form Drawer */}
      <RightDrawer
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditingCustomer(null);
        }}
        size="60"
      >
        <div className="p-1">
          <CustomerForm
            editingCustomer={editingCustomer}
            onAdd={handleAdd}
            onClose={() => {
              setOpenForm(false);
              setEditingCustomer(null);
            }}
          />
        </div>
      </RightDrawer>

      {/* Customer Detail View Drawer - compact prime design */}
      <RightDrawer
        open={showDetailView}
        title={viewingCustomer ? (viewingCustomer.companyName || viewingCustomer.name) : 'Customer details'}
        onClose={() => {
          setShowDetailView(false);
          setViewingCustomer(null);
        }}
        size="xl"
      >
        <CustomerDetailView customer={viewingCustomer} />
      </RightDrawer>
    </div>
  );
}
